import React, {
    forwardRef,
    useMemo,
    useEffect,
    useState,
    useCallback,
    useContext,
} from 'react';
import { View, Text, BasicChip, Icon, css, colors } from '@fairygodboss/ui';
import { buildCompanyLogoSrc } from '../../Elements/CompanyLogo/constants';
import { buildMetrics } from '../utils';
import AuthWrapperComponent from '../../../CallToAction/AuthWrapperComponent/AuthWrapperComponent';
import VisibilityPixel, {
    EmitMetric,
} from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import { googleAnalytic } from '../../../../pages/googeAnalyticHelper';
import { handleFiringSearchEmitterOnSearchResult } from '../../searchEmitters';
import {
    JobSearchContext,
    PageContext,
} from '../../../../contexts/ContextCreator';

const chipVariantMap = {
    remote: { variant: 'primary', text: 'Remote' },
    hiring: { variant: 'info', text: 'Actively hiring' },
    sponsored: { variant: 'success', text: 'Sponsored' },
};

export const JobSearchCard = forwardRef(
    (
        {
            isMobile,
            onJobSelect = null,
            chips = [],
            jobTitle = '',
            location = '',
            companyImageUrl = '',
            isSaved,
            isActive = false,
            hash,
            originalApplyUrl,
            category,
            company,
            title,
            searchTracking,
            ctsName,
            onSaveJob,
            isSponsored,
        },
        ref
    ) => {
        const companyLogoSrc = buildCompanyLogoSrc(companyImageUrl);
        const [isReadyForMetric, setIsReadyForMetric] = useState();
        const [metricFired, setIsMetricFired] = useState(false);
        const { user } = useContext(PageContext);
        const { fullSearchQueryId } = useContext(JobSearchContext);
        useEffect(() => {
            if (isSaved !== undefined) {
                setIsReadyForMetric(true);
            }
        }, [isSaved]);

        const handleJobClick = useCallback(() => {
            const jobClickMetric = buildMetrics({
                type: 'click',
                originalApplyUrl,
                category,
                company,
                hash,
                title,
                location,
                jobSearchQueryID: searchTracking?.queryID,
                position: searchTracking?.positionInListing,
                miscEventType: null,
                ctsName,
                ctsEvent: 'VIEW',
            });

            jobClickMetric.snowPlow = {
                component: 'tile',
                tileType: 'job',
                position: 'left_rail',
                pageType: 'collection',
                pageSection: 'search',
                targetPageSection: 'search',
                searchId: `${fullSearchQueryId}`,
                companyId: company.id,
                isSponsored,
                tileLocation: 'search',
                tileEntityId: hash,
                tileOrder: searchTracking?.positionInListing,
            };

            const isSearchQueryTracking =
                searchTracking?.positionInListing &&
                searchTracking?.queryID &&
                searchTracking?.indexName &&
                ctsName;

            if (isSearchQueryTracking) {
                handleFiringSearchEmitterOnSearchResult({
                    queryID: searchTracking?.queryID,
                    hash,
                    ctsName,
                });
            }

            // fire click metric
            EmitMetric(jobClickMetric);

            // handle google analytics
            googleAnalytic('Click', 'JobsV3', company.name);

            if (onJobSelect) return onJobSelect();
        }, [
            onJobSelect,
            category,
            company,
            hash,
            location,
            originalApplyUrl,
            title,
            searchTracking,
            ctsName,
        ]);

        return useMemo(() => {
            const pixelMetrics = buildMetrics({
                type: 'impression',
                originalApplyUrl,
                category,
                company,
                hash,
                title,
                location,
                jobSearchQueryID: searchTracking?.queryID,
                position: searchTracking?.positionInListing,
                miscEventType: null,
                ctsName,
                ctsEvent: 'IMPRESSION',
            });
            pixelMetrics.snowPlow = {
                component: 'tile',
                tileType: 'job',
                position: 'left_rail',
                pageType: 'collection',
                pageSection: 'search',
                searchId: `${fullSearchQueryId}`,
                companyId: company.id,
                isSponsored,
                tileLocation: 'search',
                tileEntityId: hash,
                tileOrder: searchTracking?.positionInListing,
            };

            const onJobClick = () => {
                handleJobClick();
            };

            return (
                <View
                    data-qa="Jobs-Search-Results-Job-Card"
                    ref={ref}
                    key={hash}
                    height="90px"
                    as="button"
                    css={css({
                        border: 'none',
                        cursor: 'pointer',
                        background:
                            isActive && !isMobile ? colors.primary_10 : 'none',
                        color: 'inherit',
                        padding: '0',
                        font: 'inherit',
                        textAlign: 'left',
                        borderBottom: '1px',
                        borderBottomStyle: 'solid',
                        width: '100%',
                        borderBottomColor: colors.neutral_20,
                        ':hover': {
                            background: colors.secondary[10],
                        },
                        ':focus': {
                            outline: '0px',
                        },
                    })}
                    onClick={onJobClick}
                >
                    {isReadyForMetric && !metricFired && (
                        <VisibilityPixel
                            metrics={pixelMetrics}
                            tracking={searchTracking?.queryID}
                            visibilityCallback={() => setIsMetricFired(true)}
                            singleFire={pixelMetrics.job_slug}
                        />
                    )}
                    <View
                        p="16px"
                        display="flex"
                        justifyContent="space-between"
                        position="inherit"
                    >
                        <View display="flex" pr="24px">
                            <View width="60px" height="60px">
                                <img
                                    style={{
                                        height: '60px',
                                        width: '60px',
                                        transform: 'translateZ(0)',
                                        imageRendering: 'crisp-edges',
                                        msInterpolationMode: 'nearest-neighbor',
                                    }}
                                    className="company-logo-image"
                                    src={companyLogoSrc}
                                    alt="company-logo"
                                />
                            </View>
                            <View pl="20px" display="grid">
                                <View pb="4px">
                                    {chips?.map((c) => (
                                        <BasicChip
                                            data-qa={`Jobs-Search-Results-JobCard-chip${chipVariantMap[c]?.text}`}
                                            position="inherit"
                                            mr="8px"
                                            key={c}
                                            size="small"
                                            variant={chipVariantMap[c]?.variant}
                                            isSelected={isActive}
                                            text={chipVariantMap[c]?.text}
                                        />
                                    ))}
                                </View>
                                <Text
                                    data-qa="Jobs-Search-Results-JobCard-job-title"
                                    variant="labelLg"
                                    // as="p"
                                    pb="4px"
                                    fontWeight={600}
                                >
                                    {jobTitle}
                                </Text>
                                <Text
                                    variant="labelSm"
                                    fontWeight={400}
                                    data-qa="Jobs-Search-Results-JobCard-location"
                                >
                                    {company?.name} | {location}
                                </Text>
                            </View>
                        </View>
                        <AuthWrapperComponent
                            callToActionHash="save-job"
                            loginTrigger="save-job"
                        >
                            <View
                                data-qa="Jobs-Search-Results-JobCard-save-button"
                                position="absolute"
                                right="16px"
                                background="none"
                                color="inherit"
                                border="none"
                                height="48px"
                                width="48px"
                                marginTop="-18px"
                                marginRight="-18px"
                                justifyContent="center"
                                alignItems="center"
                                display="flex"
                                onClick={(e) => {
                                    if (user) {
                                        e.stopPropagation();
                                        EmitMetric({
                                            job_slug: hash,
                                            community_content_location:
                                                'job-search-page',
                                            community_vote_type: 'like',
                                            vote: !isSaved ? 1 : -1,
                                            snowPlow: {
                                                tileType: 'job',
                                                click: true,
                                                component: 'button',
                                                buttonType: 'job_save',
                                                buttonText: !isSaved
                                                    ? 'Save'
                                                    : 'Unsave',
                                                position: 'left_rail',
                                                pageType: 'collection',
                                                pageSection: 'search',
                                                targetPageSection: 'search',
                                                isSponsored: !!isSponsored,
                                                tileLocation: 'search',
                                                searchId: `${fullSearchQueryId}`,
                                                tileEntityId:
                                                    pixelMetrics.job_slug,
                                                companyId: company.id,
                                                tileOrder:
                                                    searchTracking?.positionInListing,
                                            },
                                        });
                                        onSaveJob({ userLiked: isSaved, hash });
                                    }
                                }}
                            >
                                <Icon
                                    variant="heart_16"
                                    fillColor={
                                        isSaved ? colors.neutral_50 : 'none'
                                    }
                                    outlineColor={colors.neutral_50}
                                />
                            </View>
                        </AuthWrapperComponent>
                    </View>
                </View>
            );
        }, [
            onSaveJob,
            chips,
            company,
            companyLogoSrc,
            hash,
            isActive,
            isMobile,
            user,
            isSaved,
            jobTitle,
            location,
            handleJobClick,
            category,
            originalApplyUrl,
            title,
            ref,
            isReadyForMetric,
            searchTracking,
            ctsName,
        ]);
    }
);

export const MemoizedJobCard = ({
    job,
    chips,
    isSM,
    activeJob,
    activeListingRef,
    onJobSelect,
    searchTracking,
    onSaveJob,
}) => {
    return useMemo(
        () => (
            <JobSearchCard
                onSaveJob={onSaveJob}
                searchTracking={searchTracking}
                isMobile={!isSM}
                chips={chips}
                isActive={activeJob === job.hash}
                ref={activeJob === job.hash ? activeListingRef : null}
                onJobSelect={() => onJobSelect(job)}
                jobTitle={job.title}
                key={`memo-${job.hash}`}
                hash={job.hash}
                companyName={job?.company}
                location={job.location}
                isSaved={job?.userLiked}
                companyImageUrl={job?.companyLogo?.src}
                originalApplyUrl={job?.originalApplyUrl}
                category={job?.category}
                company={job?.company}
                title={job?.title}
                isSponsored={job?.isSponsored}
                ctsName={job?.ctsName}
            />
        ),
        [
            job,
            chips,
            isSM,
            activeJob,
            activeListingRef,
            onJobSelect,
            searchTracking,
            onSaveJob,
        ]
    );
};
